import React from "react";
import { Fragment } from "react";

import { css } from "@emotion/core";

import { DateTime, Dateable } from "@zapier/date";
import { Colors, Link, Typography } from "@zapier/design-system";

import { MeteredTiersTable } from "@zapier/checkout/esm/components";
import { Interval, InvoiceType, PlanTier } from "@zapier/checkout/esm/types";
import { Currency, Price } from "@zapier/currency";

type AmountDueOnNextIntervalProps = {
  currency?: Currency;
  extraTerm?: string | null;
  isPlanMetered: boolean;
  meteredTiers: PlanTier[];
  nextPaymentDate: Dateable;
  planBillingAmount: number;
  planInterval: Interval;
  planNumFreeMeteredTasks: number;
  invoiceType?: InvoiceType;
};

const nextIntervalCss = css`
  ${Typography.smallPrint1};
  background-color: ${Colors.neutral200};
  color: ${Colors.neutral800};
  padding: 1.25em;
`;

const lastParagraphCss = css`
  margin: 0;
`;

const tableStyle = css`
  margin-bottom: 15px;
`;

export const AmountDueOnNextInterval = ({
  currency,
  extraTerm = null,
  isPlanMetered,
  meteredTiers,
  nextPaymentDate,
  planBillingAmount,
  planInterval,
  planNumFreeMeteredTasks,
  invoiceType,
}: AmountDueOnNextIntervalProps) => {
  const currencyIsUSD = currency?.name === "USD",
    isOverdueInvoice = invoiceType === InvoiceType.overdue;

  const amount = (
    <strong>
      <Price currency={currency}>{planBillingAmount}</Price>
    </strong>
  );

  return (
    <div css={nextIntervalCss}>
      <p>
        You authorize Zapier to charge you for the amount above.{" "}
        {!currencyIsUSD &&
          !isOverdueInvoice &&
          "Price shown is valid for 24 hours."}
      </p>
      <p>
        Your subscription renews each {planInterval} until you cancel. The next
        charge will be {amount}, minus any applicable discounts,
        {nextPaymentDate && (
          <Fragment>
            {" "}
            on{" "}
            <strong>
              <DateTime date={nextPaymentDate} />
            </strong>
            {currencyIsUSD ? "." : ","}{" "}
          </Fragment>
        )}
        {!currencyIsUSD && (
          <Fragment>
            unless the currency exchange rate changes. The actual price is
            calculated using the exchange rate at the time of payment. Price
            charged excludes all taxes, levies and duties.
          </Fragment>
        )}
      </p>
      {extraTerm && <p>{extraTerm}</p>}
      {isPlanMetered && (
        <Fragment>
          Your plan includes{" "}
          <strong>{planNumFreeMeteredTasks.toLocaleString()} tasks</strong> per
          month. Extra tasks used will be charged monthly at the below rates.
        </Fragment>
      )}
      {isPlanMetered && (
        <div css={tableStyle}>
          <MeteredTiersTable tiers={meteredTiers} />
        </div>
      )}
      <p css={lastParagraphCss}>
        By clicking the button below, you understand and agree to these terms
        and Zapier’s{" "}
        <Link href="/privacy" target="_blank">
          privacy policy
        </Link>{" "}
        and{" "}
        <Link href="/terms" target="_blank">
          terms of service
        </Link>
        .
      </p>
    </div>
  );
};
